import React, { useState, useEffect } from 'react';
import './CreateYesNoModal.css';
import getApiUrl from '../utils/api';
import { usePostHog } from 'posthog-js/react';

const initialForm = {
  name: '',
  department: '',
  kra: '',
  description: '',
  frequency: '',
  selectedDays: [],
  file: null,
};

const DEFAULT_CHECKLIST_TYPE = 'Checklist';
const DEFAULT_LOCATION = 'HEAD OFFICE';
const WEEKDAYS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

export default function CreateYesNoModal({ open, onClose, onSubmit, departments = [] }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setErrors({});
    }
  }, [open]);

  const clearSelectedDaysError = () => {
    setErrors((prev) => {
      if (!prev.selectedDays) return prev;
      const { selectedDays: _omit, ...rest } = prev;
      return rest;
    });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'frequency') {
      setForm((prevForm) => {
        const nextSelectedDays = value === 'Custom' ? prevForm.selectedDays : [];
        return {
          ...prevForm,
          frequency: value,
          selectedDays: nextSelectedDays,
        };
      });

      if (value !== 'Custom') {
        clearSelectedDaysError();
      }

      return;
    }

    setForm((prevForm) => ({
      ...prevForm,
      [name]: files ? files[0] : value,
    }));
  };

  const handleDayToggle = (dayIndex) => {
    setForm((prevForm) => {
      const exists = prevForm.selectedDays.includes(dayIndex);
      const updated = exists
        ? prevForm.selectedDays.filter((value) => value !== dayIndex)
        : [...prevForm.selectedDays, dayIndex];
      updated.sort((a, b) => a - b);
      return {
        ...prevForm,
        selectedDays: updated,
      };
    });

    clearSelectedDaysError();
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Daily Checklist Name is required';
    if (!form.department.trim()) newErrors.department = 'Department is required';
    if (!form.frequency.trim()) newErrors.frequency = 'Frequency is required';
    if (form.frequency === 'Custom' && form.selectedDays.length === 0) {
      newErrors.selectedDays = 'Select at least one weekday';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: DEFAULT_CHECKLIST_TYPE,
        location: DEFAULT_LOCATION,
        department: form.department,
        kra: form.kra.trim() || null,
        description: form.description.trim() || null,
        budgetQty: null,
        frequency: form.frequency,
      };

      if (form.frequency === 'Custom') {
        payload.selectedDays = form.selectedDays;
      }

      const response = await fetch(getApiUrl('api/yesno/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to create Daily Checklist');
      }

      posthog.capture('Daily Checklist Created', { name: form.name });

      if (onSubmit) onSubmit(data?.data);

      setForm(initialForm);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating Daily Checklist:', error);
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="create-yesno-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="create-yesno-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="create-yesno-header">
          <div className="create-yesno-title-section">
            <h2 className="create-yesno-title">Add Daily Checklist</h2>
            <p className="create-yesno-subtitle">Add the required details for the new Daily Checklist</p>
          </div>
          <button className="create-yesno-close" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        {/* Modal Form */}
        <form className="create-yesno-form" onSubmit={handleSubmit}>
          {errors.submit && (
            <div className="create-yesno-error-banner">{errors.submit}</div>
          )}

          {/* Daily Checklist Name - Full Width */}
          <div className="create-yesno-form-group">
            <label className="create-yesno-label">
              Daily Checklist Name <span className="req">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Add Daily Checklist Name"
              value={form.name}
              onChange={handleChange}
              className={`create-yesno-input ${errors.name ? 'error' : ''}`}
            />
            {errors.name && <div className="create-yesno-error-text">{errors.name}</div>}
          </div>

          <div className="create-yesno-form-group">
            <label className="create-yesno-label">
              Department <span className="req">*</span>
            </label>
            <select
              name="department"
              value={form.department}
              onChange={handleChange}
              className={`create-yesno-input ${errors.department ? 'error' : ''}`}
            >
              <option value="">Select Dep.</option>
              {departments.map((dept) => (
                <option key={dept.id || dept.name} value={dept.name || dept.id}>
                  {dept.name || dept.label}
                </option>
              ))}
            </select>
            {errors.department && <div className="create-yesno-error-text">{errors.department}</div>}
          </div>

          {/* Add KRA - Full Width */}
          <div className="create-yesno-form-group">
            <label className="create-yesno-label">Add KRA</label>
            <input
              type="text"
              name="kra"
              placeholder="Add KRA"
              value={form.kra}
              onChange={handleChange}
              className="create-yesno-input"
            />
          </div>

          {/* Description - Full Width */}
          <div className="create-yesno-form-group">
            <label className="create-yesno-label">Description</label>
            <textarea
              name="description"
              placeholder="Add Description"
              value={form.description}
              onChange={handleChange}
              className="create-yesno-textarea"
              rows="3"
            />
          </div>

          <div className="create-yesno-form-group">
            <label className="create-yesno-label">
              Frequency <span className="req">*</span>
            </label>
            <select
              name="frequency"
              value={form.frequency}
              onChange={handleChange}
              className={`create-yesno-input ${errors.frequency ? 'error' : ''}`}
            >
              <option value="">Select Frequency</option>
              <option value="Daily">Daily</option>
              <option value="Alternate">Alternate</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Yearly">Yearly</option>
              <option value="Custom">Custom</option>
            </select>
            {errors.frequency && <div className="create-yesno-error-text">{errors.frequency}</div>}
          </div>

          {form.frequency === 'Custom' && (
            <div className="create-yesno-form-group">
              <label className="create-yesno-label">
                Select Weekdays <span className="req">*</span>
              </label>
              <div className="create-yesno-weekday-grid">
                {WEEKDAYS.map((day) => {
                  const isSelected = form.selectedDays.includes(day.value);
                  return (
                    <button
                      type="button"
                      key={day.value}
                      className={`create-yesno-weekday-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleDayToggle(day.value)}
                      aria-pressed={isSelected}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              {errors.selectedDays && (
                <div className="create-yesno-error-text">{errors.selectedDays}</div>
              )}
            </div>
          )}

          {/* Choose File - Full Width */}
          <div className="create-yesno-form-group">
            <label className="create-yesno-label">Choose File</label>
            <input
              type="file"
              name="file"
              onChange={handleChange}
              className="create-yesno-file-input"
            />
          </div>

          {/* Form Actions */}
          <div className="create-yesno-actions">
            <button
              type="submit"
              className="create-yesno-btn-submit"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



